import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Clock, ArrowLeft, User, Stethoscope } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import toast from 'react-hot-toast';

const RescheduleAppointment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [appointment, setAppointment] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    appointmentDate: '',
    startTime: '',
    endTime: '',
    duration: 30,
    reason: ''
  });

  useEffect(() => {
    fetchAppointment();
  }, [id]);

  useEffect(() => {
    if (appointment && formData.appointmentDate) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [formData.appointmentDate, formData.duration, appointment]);

  const fetchAppointment = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/appointments/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAppointment(data.appointment);
        
        // Set initial form data
        if (data.appointment) {
          const appointmentDate = new Date(data.appointment.appointmentDate);
          const dateStr = appointmentDate.toISOString().split('T')[0];
          
          setFormData(prev => ({
            ...prev,
            appointmentDate: dateStr,
            startTime: data.appointment.startTime,
            endTime: data.appointment.endTime,
            duration: data.appointment.duration || 30
          }));
        }
      } else {
        toast.error('Failed to load appointment details');
        navigate('/appointments');
      }
    } catch (error) {
      console.error('Error fetching appointment:', error);
      toast.error('Error loading appointment');
      navigate('/appointments');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!appointment?.doctor || !formData.appointmentDate) return;

    try {
      setLoadingSlots(true);
      const dateStr = formData.appointmentDate;
      const response = await fetch(
        `${API_BASE_URL}/api/appointments/availability/${appointment.doctor._id || appointment.doctor}?date=${dateStr}&duration=${formData.duration}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.availableSlots || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.message || 'Failed to load available slots');
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      toast.error('Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setFormData(prev => ({ ...prev, appointmentDate: date, startTime: '', endTime: '' }));
  };

  const handleSlotSelect = (slot) => {
    setFormData(prev => ({
      ...prev,
      startTime: slot.startTime,
      endTime: slot.endTime
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.appointmentDate) {
      toast.error('Please select a date');
      return;
    }

    if (!formData.startTime || !formData.endTime) {
      toast.error('Please select a time slot');
      return;
    }

    try {
      setSubmitting(true);

      // Format appointment date as ISO string
      const appointmentDateISO = new Date(formData.appointmentDate).toISOString();

      const requestBody = {
        appointmentDate: appointmentDateISO,
        startTime: formData.startTime,
        endTime: formData.endTime,
        reason: formData.reason
      };

      const response = await fetch(`${API_BASE_URL}/api/appointments/${id}/reschedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Appointment rescheduled successfully!');
        navigate('/appointments');
      } else {
        toast.error(data.message || 'Failed to reschedule appointment');
        console.error('Reschedule error:', data);
      }
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast.error('An error occurred while rescheduling the appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!appointment) {
    return null;
  }

  const doctorName = appointment.doctor?.name || 'Unknown Doctor';
  const doctorSpecialization = appointment.doctor?.doctorProfile?.specialization || '';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/appointments"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Appointments
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Reschedule Appointment</h1>
          <p className="text-gray-600 mt-2">Change the date and time of your appointment</p>
        </div>

        {/* Current Appointment Info */}
        <div className="card mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                <Stethoscope className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-semibold text-gray-900">Current Appointment</h3>
              <div className="mt-2 space-y-1">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Doctor:</span> {doctorName}
                  {doctorSpecialization && ` - ${doctorSpecialization}`}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Date:</span> {formatDate(appointment.appointmentDate)}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Time:</span> {appointment.startTime} - {appointment.endTime}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Status:</span> 
                  <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 capitalize">
                    {appointment.status}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="card space-y-6">
          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Appointment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.appointmentDate}
              onChange={handleDateChange}
              min={getMinDate()}
              className="input-field w-full"
              required
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration (minutes)
            </label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                duration: parseInt(e.target.value),
                startTime: '',
                endTime: ''
              }))}
              className="input-field w-full"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
            </select>
          </div>

          {/* Available Time Slots */}
          {formData.appointmentDate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Time Slot <span className="text-red-500">*</span>
              </label>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                  <span className="ml-2 text-gray-600">Loading available slots...</span>
                </div>
              ) : availableSlots.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availableSlots.map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSlotSelect(slot)}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.startTime === slot.startTime
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                      }`}
                    >
                      <Clock className="h-4 w-4 mx-auto mb-1" />
                      <div className="text-sm font-medium">
                        {slot.startTime} - {slot.endTime}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    No available time slots for this date. Please select another date.
                  </p>
                </div>
              )}
              {formData.startTime && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Selected: <span className="font-medium">{formData.startTime} - {formData.endTime}</span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Rescheduling (Optional)
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              rows={3}
              placeholder="Please provide a reason for rescheduling..."
              className="input-field w-full"
              maxLength={200}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.reason.length}/200 characters
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <Link
              to="/appointments"
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting || !formData.appointmentDate || !formData.startTime}
              className="btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Rescheduling...
                </>
              ) : (
                'Reschedule Appointment'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RescheduleAppointment;

